import { FC, useState, useEffect } from 'react';
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Divider,
  Link,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  useFinancialGrantsState,
  useFinancialGrantsActions,
} from '../../contexts/financialGrants.context';
import { ArrowBack } from '@mui/icons-material';
import {
  TFinancialGrant,
  GrantStatus,
} from '../../../../../apis/finance/financialGrants.interfaces';
import { ECurrencySymbol } from '../../../../../apis/__shared/currencyEnum';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useFinanceService } from 'src/apis/finance/useFinanceService';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';

enum EFinancialGrantView {
  LIST = 'list',
  DETAILS = 'details',
}

interface FinancialGrantsGridProps {
  isLoading?: boolean;
}

const FinancialGrantsGrid: FC<FinancialGrantsGridProps> = () => {
  const { translateText } = useTranslation();
  const { financialGrantsList, financialGrantsAppState } =
    useFinancialGrantsState();
  const { getFinanceGrantById } = useFinanceService();
  const {
    setSelectedFinancialGrantItem,
    setSelectedFinancialGrantItemId,
    setIsManageFinancialGrantItemOpen,
    setIsCreateMode,
    setIsEditMode,
  } = useFinancialGrantsActions();
  const [status, setStatus] = useState<GrantStatus>(GrantStatus.ALL);
  const [viewType, setViewType] = useState<EFinancialGrantView>(
    EFinancialGrantView.LIST
  );
  const [filteredFinancialGrants, setFilteredFinancialGrants] = useState<
    TFinancialGrant[]
  >([]);

  useEffect(() => {
    setFilteredFinancialGrants(
      financialGrantsList.filter(
        (financialGrant) =>
          status === GrantStatus.ALL || financialGrant.status === status
      )
    );
  }, [status, financialGrantsList]);

  // Sync viewType with context state
  useEffect(() => {
    if (financialGrantsAppState.selectedFinancialGrantItem) {
      setViewType(EFinancialGrantView.DETAILS);
    } else if (!financialGrantsAppState.selectedFinancialGrantItemId) {
      setViewType(EFinancialGrantView.LIST);
    }
  }, [
    financialGrantsAppState.selectedFinancialGrantItem,
    financialGrantsAppState.selectedFinancialGrantItemId,
  ]);

  useEffect(() => {
    const getGrantDetails = async (grantId: string) => {
      // First check if grant is already in the list
      const existingGrant = financialGrantsList.find((g) => g.id === grantId);
      if (existingGrant) {
        setSelectedFinancialGrantItem(existingGrant);
      } else {
        // If not in list, fetch from API
        const grantDetails = await getFinanceGrantById(grantId);
        setSelectedFinancialGrantItem(grantDetails || null);
      }
    };
    if (financialGrantsAppState?.selectedFinancialGrantItemId) {
      getGrantDetails(financialGrantsAppState?.selectedFinancialGrantItemId);
    }
    // eslint-disable-next-line
  }, [
    financialGrantsAppState?.selectedFinancialGrantItemId,
    financialGrantsList,
  ]);

  const getFinancialGrantsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'name',
        headerName: translateText('finance.grants.columns.name'),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'amount',
        headerName: translateText('finance.grants.columns.amount'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TFinancialGrant;
          return `${ECurrencySymbol[data.currency]}${data.amount}`;
        },
      },
      {
        field: 'applicationDeadline',
        headerName: translateText('finance.grants.columns.date'),
        width: 120,
        type: 'date',
        valueFormatter: (params: any) => {
          if (!params?.value) return '';
          try {
            return new Date(params.value).toLocaleDateString();
          } catch {
            return '';
          }
        },
      },
      {
        field: 'grantType',
        headerName: translateText('finance.grants.columns.type'),
        width: 120,
      },
      {
        field: 'category',
        headerName: translateText('finance.grants.columns.category'),
        width: 150,
      },
      {
        field: 'status',
        headerName: translateText('finance.grants.columns.status'),
        width: 120,
        renderCell: (params) => {
          const status = params.value as string;
          return (
            <Chip
              label={status}
              color={
                status === GrantStatus.OPEN
                  ? 'success'
                  : status === GrantStatus.AWARDED
                    ? 'info'
                    : status === GrantStatus.DISBURSED
                      ? 'primary'
                      : status === GrantStatus.CLOSED
                        ? 'default'
                        : status === GrantStatus.EXPIRED
                          ? 'error'
                          : 'default'
              }
              size="small"
            />
          );
        },
      },
      {
        field: 'disbursementFrequency',
        headerName: translateText('finance.grants.columns.paymentMode'),
        width: 150,
      },
      {
        field: 'tags',
        headerName: translateText('finance.grants.columns.tags'),
        width: 200,
        renderCell: (params) => {
          const data = params.row as TFinancialGrant;
          return data.tags?.join(', ') || '-';
        },
      },
    ];
  };

  const handleRowClick = (params: any) => {
    if (viewType === EFinancialGrantView.LIST) {
      const grant = financialGrantsList.find((g) => g.id === params.row.id);
      if (grant) {
        setSelectedFinancialGrantItemId(grant.id);
      }
    }
  };

  const handleBackToList = () => {
    setViewType(EFinancialGrantView.LIST);
    setSelectedFinancialGrantItem(null);
    setSelectedFinancialGrantItemId(null);
  };

  const handleAddGrant = () => {
    setIsManageFinancialGrantItemOpen(true);
    setIsCreateMode(true);
    setIsEditMode(false);
  };

  const handleEditGrant = () => {
    setIsManageFinancialGrantItemOpen(true);
    setIsCreateMode(false);
    setIsEditMode(true);
  };

  return (
    <Grid
      container
      spacing={0}
      sx={{ height: '100%' }}
      data-testid="financial-grants-grid-container"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {viewType === EFinancialGrantView.DETAILS &&
          financialGrantsAppState.selectedFinancialGrantItem ? (
          <Box
            display="flex"
            alignItems="center"
            data-testid="financial-grant-detail-header"
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToList}
              sx={{ mr: 2 }}
              data-testid="back-to-list-button"
            >
              {translateText('finance.grants.backToList')}
            </Button>
            <Typography variant="h6" data-testid="detail-header-title">
              {financialGrantsAppState.selectedFinancialGrantItem.name ||
                translateText('finance.grants.nA')}
            </Typography>
          </Box>
        ) : (
          <ToggleButtonGroup
            size="medium"
            color="primary"
            exclusive
            value={status}
            onChange={(_event, value) => setStatus(value)}
            aria-label={translateText('finance.grants.financeView')}
            sx={{ height: 'var(--tab-group-height)' }}
            data-testid="financial-grants-view-toggle-button-group"
          >
            <ToggleButton
              value={GrantStatus.ALL}
              data-testid="financial-grants-view-toggle-button-all"
            >
              {translateText('finance.grants.filters.all')}
            </ToggleButton>
            <ToggleButton
              value={GrantStatus.OPEN}
              data-testid="financial-grants-view-toggle-button-open"
            >
              {translateText('finance.grants.filters.open')}
            </ToggleButton>
            <ToggleButton
              value={GrantStatus.CLOSED}
              data-testid="financial-grants-view-toggle-button-closed"
            >
              {translateText('finance.grants.filters.closed')}
            </ToggleButton>
            <ToggleButton
              value={GrantStatus.AWARDED}
              data-testid="financial-grants-view-toggle-button-awarded"
            >
              {translateText('finance.grants.filters.awarded')}
            </ToggleButton>
            <ToggleButton
              value={GrantStatus.DISBURSED}
              data-testid="financial-grants-view-toggle-button-disbursed"
            >
              {translateText('finance.grants.filters.disbursed')}
            </ToggleButton>
            <ToggleButton
              value={GrantStatus.EXPIRED}
              data-testid="financial-grants-view-toggle-button-expired"
            >
              {translateText('finance.grants.filters.expired')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {viewType === EFinancialGrantView.LIST ? (
          <AddButton
            label={translateText('finance.grants.addGrant')}
            onClickHandler={handleAddGrant}
            data-testid="add-grant-button"
          />
        ) : (
          <EditButton
            label={translateText('finance.grants.editGrant')}
            onClickHandler={handleEditGrant}
            data-testid="edit-grant-button"
          />
        )}
      </Grid>

      {viewType === EFinancialGrantView.DETAILS &&
        financialGrantsAppState.selectedFinancialGrantItem && (
          <Grid
            item
            xs={12}
            sx={{ mb: 2 }}
            data-testid="financial-grant-detail-info-container"
          >
            {/* Basic Details Section */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 1.5 }}
                  data-testid="grant-details-title"
                >
                  {translateText('finance.grants.grantDetails')}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={12} sm={6} md={3} data-testid="detail-name">
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.columns.name')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .name || translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="detail-amount">
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.columns.amount')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {
                        ECurrencySymbol[
                        financialGrantsAppState.selectedFinancialGrantItem
                          .currency
                        ]
                      }
                      {financialGrantsAppState.selectedFinancialGrantItem.amount.toFixed(
                        2
                      )}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-currency"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.currency')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .currency || translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="detail-type">
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.columns.type')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .grantType || translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-category"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.columns.category')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .category || translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="detail-status">
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.columns.status')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={
                          financialGrantsAppState.selectedFinancialGrantItem
                            .status || translateText('finance.grants.nA')
                        }
                        color={
                          financialGrantsAppState.selectedFinancialGrantItem
                            .status === GrantStatus.OPEN
                            ? 'success'
                            : financialGrantsAppState.selectedFinancialGrantItem
                              .status === GrantStatus.AWARDED
                              ? 'info'
                              : financialGrantsAppState
                                .selectedFinancialGrantItem.status ===
                                GrantStatus.DISBURSED
                                ? 'primary'
                                : financialGrantsAppState
                                  .selectedFinancialGrantItem.status ===
                                  GrantStatus.CLOSED
                                  ? 'default'
                                  : financialGrantsAppState
                                    .selectedFinancialGrantItem.status ===
                                    GrantStatus.EXPIRED
                                    ? 'error'
                                    : 'default'
                        }
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-application-status"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.form.applicationStatus')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .applicationStatus ||
                        translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-disbursement-frequency"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.disbursementFrequency')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .disbursementFrequency ||
                        translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  {financialGrantsAppState.selectedFinancialGrantItem
                    .description && (
                      <Grid item xs={12} data-testid="detail-description">
                        <Typography variant="caption" color="textSecondary">
                          {translateText('finance.grants.description')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {
                            financialGrantsAppState.selectedFinancialGrantItem
                              .description
                          }
                        </Typography>
                      </Grid>
                    )}
                  {financialGrantsAppState.selectedFinancialGrantItem.tags &&
                    financialGrantsAppState.selectedFinancialGrantItem.tags
                      .length > 0 && (
                      <Grid item xs={12} data-testid="detail-tags">
                        <Typography variant="caption" color="textSecondary">
                          {translateText('finance.grants.columns.tags')}
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                          }}
                        >
                          {financialGrantsAppState.selectedFinancialGrantItem.tags.map(
                            (tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                variant="outlined"
                              />
                            )
                          )}
                        </Box>
                      </Grid>
                    )}
                </Grid>
              </CardContent>
            </Card>

            {/* Eligibility Criteria Section */}
            {financialGrantsAppState.selectedFinancialGrantItem
              .eligibilityCriteria && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mb: 1.5 }}
                      data-testid="eligibility-criteria-title"
                    >
                      {translateText('finance.grants.form.eligibilityCriteria')}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .eligibilityCriteria.gpaRequirement !== undefined && (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            data-testid="detail-gpa-requirement"
                          >
                            <Typography variant="caption" color="textSecondary">
                              {translateText('finance.grants.form.gpaRequirement')}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {
                                financialGrantsAppState.selectedFinancialGrantItem
                                  .eligibilityCriteria.gpaRequirement
                              }
                            </Typography>
                          </Grid>
                        )}
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .eligibilityCriteria.incomeThreshold !== undefined && (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            data-testid="detail-income-threshold"
                          >
                            <Typography variant="caption" color="textSecondary">
                              {translateText('finance.grants.form.incomeThreshold')}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {
                                ECurrencySymbol[
                                financialGrantsAppState.selectedFinancialGrantItem
                                  .currency
                                ]
                              }
                              {
                                financialGrantsAppState.selectedFinancialGrantItem
                                  .eligibilityCriteria.incomeThreshold
                              }
                            </Typography>
                          </Grid>
                        )}
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .eligibilityCriteria.documentsRequired &&
                        financialGrantsAppState.selectedFinancialGrantItem
                          .eligibilityCriteria.documentsRequired.length > 0 && (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            data-testid="detail-documents-required"
                          >
                            <Typography variant="caption" color="textSecondary">
                              {translateText(
                                'finance.grants.form.documentsRequired'
                              )}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {financialGrantsAppState.selectedFinancialGrantItem.eligibilityCriteria.documentsRequired.join(
                                ', '
                              )}
                            </Typography>
                          </Grid>
                        )}
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .eligibilityCriteria.additionalRequirements &&
                        financialGrantsAppState.selectedFinancialGrantItem
                          .eligibilityCriteria.additionalRequirements.length >
                        0 && (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            data-testid="detail-additional-requirements"
                          >
                            <Typography variant="caption" color="textSecondary">
                              {translateText(
                                'finance.grants.form.additionalRequirements'
                              )}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {financialGrantsAppState.selectedFinancialGrantItem.eligibilityCriteria.additionalRequirements.join(
                                ', '
                              )}
                            </Typography>
                          </Grid>
                        )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

            {/* Dates Section */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 1.5 }}
                  data-testid="dates-title"
                >
                  {translateText('finance.grants.form.dates')}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-application-deadline"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.applicationDeadline')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .applicationDeadline
                        ? new Date(
                          financialGrantsAppState.selectedFinancialGrantItem.applicationDeadline
                        ).toLocaleDateString()
                        : translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  {financialGrantsAppState.selectedFinancialGrantItem
                    .awardDate && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        data-testid="detail-award-date"
                      >
                        <Typography variant="caption" color="textSecondary">
                          {translateText('finance.grants.form.awardDate')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {new Date(
                            financialGrantsAppState.selectedFinancialGrantItem.awardDate
                          ).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                  {financialGrantsAppState.selectedFinancialGrantItem.validity
                    ?.startDate && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        data-testid="detail-validity-start-date"
                      >
                        <Typography variant="caption" color="textSecondary">
                          {translateText('finance.grants.form.validityStartDate')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {new Date(
                            financialGrantsAppState.selectedFinancialGrantItem.validity.startDate
                          ).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                  {financialGrantsAppState.selectedFinancialGrantItem.validity
                    ?.endDate && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        data-testid="detail-validity-end-date"
                      >
                        <Typography variant="caption" color="textSecondary">
                          {translateText('finance.grants.form.validityEndDate')}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {new Date(
                            financialGrantsAppState.selectedFinancialGrantItem.validity.endDate
                          ).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                </Grid>
              </CardContent>
            </Card>

            {/* Provider Details Section */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 1.5 }}
                  data-testid="provider-details-title"
                >
                  {translateText('finance.grants.form.providerDetails')}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-provider-id"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.form.providerId')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .providerId || translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-provider"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.provider')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .providerName || translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="detail-provider-type"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText('finance.grants.form.providerType')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .providerType || translateText('finance.grants.nA')}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Recipient Details Section */}
            {(financialGrantsAppState.selectedFinancialGrantItem.recipientId ||
              financialGrantsAppState.selectedFinancialGrantItem
                .recipientName) && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mb: 1.5 }}
                      data-testid="recipient-details-title"
                    >
                      {translateText('finance.grants.form.recipientDetails')}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .recipientId && (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            data-testid="detail-recipient-id"
                          >
                            <Typography variant="caption" color="textSecondary">
                              {translateText('finance.grants.form.recipientId')}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {
                                financialGrantsAppState.selectedFinancialGrantItem
                                  .recipientId
                              }
                            </Typography>
                          </Grid>
                        )}
                      {financialGrantsAppState.selectedFinancialGrantItem
                        .recipientName && (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            data-testid="detail-recipient-name"
                          >
                            <Typography variant="caption" color="textSecondary">
                              {translateText('finance.grants.form.recipientName')}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {
                                financialGrantsAppState.selectedFinancialGrantItem
                                  .recipientName
                              }
                            </Typography>
                          </Grid>
                        )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

            {/* Documents Section */}
            {financialGrantsAppState.selectedFinancialGrantItem.documents &&
              financialGrantsAppState.selectedFinancialGrantItem.documents
                .length > 0 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mb: 1.5 }}
                      data-testid="documents-title"
                    >
                      {translateText('common.documents') || 'Documents'}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {financialGrantsAppState.selectedFinancialGrantItem.documents.map(
                        (doc, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={doc.documentId || index}
                            data-testid={`document-item-${index}`}
                          >
                            <Card variant="outlined" sx={{ height: '100%' }}>
                              <CardContent>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  gutterBottom
                                >
                                  {doc.description || doc.documentName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                >
                                  {translateText('documents.type') || 'Type'}:{' '}
                                  {doc.documentType}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  {translateText('common.status')}: {doc.status}
                                </Typography>
                                {doc.fileUrl && (
                                  <Button
                                    size="small"
                                    component={Link}
                                    href={doc.fileUrl}
                                    target="_blank"
                                    sx={{ mt: 1 }}
                                    data-testid={`document-view-button-${index}`}
                                  >
                                    {translateText('common.view') || 'View'}
                                  </Button>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        )
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

            {/* Audit Information Section */}
            {financialGrantsAppState.selectedFinancialGrantItem.actions && (
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ mb: 1.5 }}
                    data-testid="audit-info-title"
                  >
                    {translateText('common.actions') || 'Audit Information'}
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    {financialGrantsAppState.selectedFinancialGrantItem.actions
                      .created && (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          data-testid="detail-created-by"
                        >
                          <Typography variant="caption" color="textSecondary">
                            {translateText('common.created') || 'Created By'}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {financialGrantsAppState.selectedFinancialGrantItem
                              .actions.created.name ||
                              translateText('finance.grants.nA')}
                          </Typography>
                          {financialGrantsAppState.selectedFinancialGrantItem
                            .actions.created.timestamp && (
                              <Typography variant="caption" color="textSecondary">
                                {new Date(
                                  financialGrantsAppState.selectedFinancialGrantItem.actions.created.timestamp
                                ).toLocaleString()}
                              </Typography>
                            )}
                        </Grid>
                      )}
                    {financialGrantsAppState.selectedFinancialGrantItem.actions
                      .updated && (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          data-testid="detail-updated-by"
                        >
                          <Typography variant="caption" color="textSecondary">
                            {translateText('common.updated') || 'Updated By'}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {financialGrantsAppState.selectedFinancialGrantItem
                              .actions.updated.name ||
                              translateText('finance.grants.nA')}
                          </Typography>
                          {financialGrantsAppState.selectedFinancialGrantItem
                            .actions.updated.timestamp && (
                              <Typography variant="caption" color="textSecondary">
                                {new Date(
                                  financialGrantsAppState.selectedFinancialGrantItem.actions.updated.timestamp
                                ).toLocaleString()}
                              </Typography>
                            )}
                        </Grid>
                      )}
                    {financialGrantsAppState.selectedFinancialGrantItem.actions
                      .deleted && (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          data-testid="detail-deleted-by"
                        >
                          <Typography variant="caption" color="textSecondary">
                            {translateText('common.deleted') || 'Deleted By'}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {financialGrantsAppState.selectedFinancialGrantItem
                              .actions.deleted.name ||
                              translateText('finance.grants.nA')}
                          </Typography>
                          {financialGrantsAppState.selectedFinancialGrantItem
                            .actions.deleted.timestamp && (
                              <Typography variant="caption" color="textSecondary">
                                {new Date(
                                  financialGrantsAppState.selectedFinancialGrantItem.actions.deleted.timestamp
                                ).toLocaleString()}
                              </Typography>
                            )}
                        </Grid>
                      )}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Grid>
        )}

      {viewType === EFinancialGrantView.LIST && (
        <Grid
          item
          xs={12}
          data-testid="financial-grants-grid-wrapper"
          sx={{
            height: 'calc(100vh - 250px)',
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            data-testid="financial-grants-grid"
            rows={filteredFinancialGrants}
            columns={getFinancialGrantsGridColumns()}
            onRowClick={handleRowClick}
            getRowId={(row) => {
              if (!row || !row.id) {
                console.warn('Invalid row data:', row);
                return '';
              }
              return row.id;
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default FinancialGrantsGrid;
